'use client';

import { useCompleteTrackingContext } from '@hc-frontend/features-tracking';
import { useEffect, useMemo } from 'react';

import {
  DOMAIN_SESSION_ID,
  DOMAIN_USER_ID,
  SESSION_ID,
  USER_ID,
} from './constants';
import { HouseholdStep } from './household-step';
import { LocationStep } from './location-step';
import { ProfileStep } from './profile-step';
import {
  SubsidyCalculatorProvider,
  SubsidyCalculatorSessionParameters,
  SubsidyCalculatorTrackingParameters,
  useSubsidyCalculator,
} from './provider';
import { ResultsStep } from './results-step';
import { StepIndicator } from './step-indicator';
import { useComponentImpression } from './use-component-impression';

type SubsidyCalculatorProps = {
  logo: string;
  title?: string;
  subtitle?: string;
  className?: string;
  trackingParameters?: SubsidyCalculatorTrackingParameters;
  sessionParameters: SubsidyCalculatorSessionParameters;
};

function replacePlaceholders({
  text,
  placeholder,
  value,
}: {
  text: string;
  placeholder: string;
  value: string;
}) {
  return text.replace(new RegExp(`{${placeholder}}`, 'g'), value);
}

function Steps() {
  const { step, setAnswer, sessionParameters } = useSubsidyCalculator();

  useEffect(() => {
    setAnswer(SESSION_ID, sessionParameters.sessionId);
    setAnswer(USER_ID, sessionParameters.userId);
    setAnswer(DOMAIN_SESSION_ID, sessionParameters.domainSessionId);
    setAnswer(DOMAIN_USER_ID, sessionParameters.domainUserId);
  }, [sessionParameters]);

  useComponentImpression(`subsidy-calculator-step-${step}`);

  if (step === 0) {
    return <LocationStep />;
  }

  if (step === 1) {
    return <HouseholdStep />;
  }

  if (step === 2) {
    return <ProfileStep />;
  }

  return <ResultsStep />;
}

export default function SubsidyCalculator({
  title = 'Title',
  subtitle = 'Subtitle',
  logo,
  sessionParameters,
  className,
  trackingParameters,
}: Readonly<SubsidyCalculatorProps>) {
  const { tracker } = useCompleteTrackingContext();

  const sessionParametersWithDomain = useMemo(() => {
    return {
      ...sessionParameters,
      domainSessionId: tracker.getDomainUserInfo()[6],
      domainUserId: tracker.getDomainUserId(),
    };
  }, [sessionParameters, tracker]);

  return (
    <SubsidyCalculatorProvider
      sessionParameters={sessionParametersWithDomain}
      trackingParameters={trackingParameters}
    >
      <div className={className}>
        <div className="bg-primary-900 text-gray-0 p-16-32 rounded-t-lg tablet:rounded-t-3xl overflow-hidden">
          <img
            className="object-contain mb-12-12"
            width={200}
            height={50}
            src={logo}
            alt="Subsidy calculator logo"
          />
          <p className="hc-typography--heading-four">{title}</p>
          <SubTitle text={subtitle} />
        </div>
        <div className="bg-gray-0 p-16-32 border-x border-b border-gray-50 rounded-b-lg tablet:rounded-b-3xl">
          <div className="flex mb-24-24 justify-between relative before:content-[''] before:absolute before:top-[16px] before:left-0 before:right-0 before:h-[2px] before:bg-gray-50 before:transform before:-translate-y-1/2 before:z-0">
            <StepIndicator number={1} label="Location" />
            <StepIndicator number={2} label="Household" />
            <StepIndicator number={3} label="Profile" />
            <StepIndicator number={4} label="Results" />
          </div>
          <Steps />
        </div>
      </div>
    </SubsidyCalculatorProvider>
  );
}

export function SubTitle({ text }: { text: string }) {
  const { answers } = useSubsidyCalculator();
  const planYear = answers.PLAN_YEAR;

  const subtitleValue = replacePlaceholders({
    text,
    placeholder: 'planYear',
    value: planYear ?? '2025',
  });

  return <p className="hc-typography--paragraph-sm">{subtitleValue}</p>;
}
